import { Grid, GridSize } from '@material-ui/core';
import { useMemo } from 'react';

// Data Layer
import {
  deductibleCoverages,
  endorsementCoverages,
} from 'data-layer-cr/dist/functions/coverages';
import {
  defaultUnitDriver,
  validLegalEntity,
} from 'data-layer-cr/dist/functions/quotations';
import { PaddedContent } from 'design-system';

// Components
import {
  CustomerDetailsCard,
  DriverDetailsCard,
  SummaryCarDetailsCard,
} from '../../../../components';
import AdditionalCoverageCard from '../../AdditionalCoveragesCard';
import CoverageDetailsCard from '../../CoverageDetailsCard';
import DeductibleSectionWrapper from '../../DeductibleSectionWrapper';

// Shared props
import PricingProfileResponse from '../../../../../../../models/responseTypes/PricingProfileResponse';
import DetailsContentProps from '../DetailsContentProps';

export type DataDesktopGridSize = {
  containerGridSize: GridSize;
  customerDataGridSize: GridSize;
  carDataGridSize: GridSize;
};

interface RegularDetailsProps extends DetailsContentProps {
  differentDriver: boolean;
  currentProfile: PricingProfileResponse;
}

const RegularDetails = ({
  quotation,
  coverageCatalogs,
  coverageCatalogResumes,
  differentDriver,
  currentProfile,
  additionalBenefits,
}: RegularDetailsProps): JSX.Element => {
  const hasDeductibleCoverages = useMemo(
    () => deductibleCoverages(coverageCatalogs).length > 0,
    [coverageCatalogs],
  );
  const hasEndorsementCoverages = useMemo(
    () => endorsementCoverages(coverageCatalogs).length > 0,
    [coverageCatalogs],
  );

  const driver = useMemo(() => defaultUnitDriver(quotation), [quotation]);

  // only display driverDetailsCard if driver and customers are different or customerType is Legal Entity
  const displayDriverCard = useMemo(
    () => differentDriver || quotation.customerType === 'J',
    [differentDriver, quotation],
  );

  const { containerGridSize, customerDataGridSize, carDataGridSize } =
    useMemo((): DataDesktopGridSize => {
      if (hasDeductibleCoverages || hasEndorsementCoverages) {
        return {
          containerGridSize: 4,
          customerDataGridSize: 12,
          carDataGridSize: 12,
        };
      }
      return {
        containerGridSize: 8,
        customerDataGridSize: 6,
        carDataGridSize: 6,
      };
    }, [hasDeductibleCoverages, hasEndorsementCoverages]);

  return (
    <PaddedContent data-testid="content">
      <Grid
        container
        spacing={3}
        alignItems="flex-start"
        justifyContent="center"
      >
        {/* Deductibles section */}
        {hasDeductibleCoverages && (
          <Grid
            data-testid="deductible-container"
            item
            xs={12}
            md={containerGridSize}
          >
            <DeductibleSectionWrapper
              data-testid="deductible-section"
              quotation={quotation}
              coverageCatalogs={coverageCatalogs}
              coverageCatalogResumes={coverageCatalogResumes}
              deductibleGridSize={carDataGridSize}
              currentProfile={currentProfile}
            />
          </Grid>
        )}
        {/* Customer and car section */}
        <Grid data-testid="data-container" item xs={12} md={containerGridSize}>
          <Grid container spacing={2}>
            <Grid data-testid="car-container" item xs={12} md={carDataGridSize}>
              <SummaryCarDetailsCard
                data-testid="car-section"
                quotationSummary={quotation.summary}
              />
            </Grid>
            <Grid
              data-testid="customer-container"
              item
              xs={12}
              md={customerDataGridSize}
            >
              <CustomerDetailsCard
                data-testid="customer-section"
                customerType={quotation.customerType}
                customer={quotation.customer}
                legalEntity={validLegalEntity(quotation)}
                currentProfile={currentProfile}
              />
            </Grid>

            {displayDriverCard && (
              <Grid item xs={12} md={customerDataGridSize}>
                <DriverDetailsCard
                  data-testid="driver-section"
                  firstName={driver.firstName}
                  lastName={driver.lastName}
                  gender={driver.gender}
                  birthdate={driver.birthday}
                />
              </Grid>
            )}
            {hasEndorsementCoverages && (
              <Grid
                item
                xs={12}
                md={customerDataGridSize}
                data-testid="deductible-endorsement-container"
              >
                <AdditionalCoverageCard
                  data-testid="endorsement-section"
                  quotation={quotation}
                  coverages={coverageCatalogs}
                  currentProfileTypeOfMoney={currentProfile.typeOfMoney}
                  additionalBenefits={additionalBenefits}
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* Coverages Section */}
        <Grid item xs={12} md={containerGridSize}>
          <CoverageDetailsCard
            data-testid="coverage-section"
            quotation={quotation}
            coverages={coverageCatalogs}
            coverageResumes={coverageCatalogResumes}
            currentProfile={currentProfile}
          />
        </Grid>
      </Grid>
    </PaddedContent>
  );
};

export default RegularDetails;
